/**
 * What is switched on behind the gear.
 *
 * Hiding the advanced settings buys a calm composer row, but it must not buy it
 * by making the reader wonder whether the panel is running. Anything set away
 * from its default gets a chip out here, and every chip can be dismissed to put
 * that one setting back.
 */

import { X } from "lucide-react";
import { MODELS } from "../../../shared/models.ts";
import type { SendSettings } from "../../state/useChat.ts";
import { Badge } from "../ui/badge.tsx";

interface SettingChipsProps {
  readonly settings: SendSettings;
  readonly defaults: SendSettings;
  readonly onChange: (settings: SendSettings) => void;
  /** Clicking a chip's body opens the popover it came from. */
  readonly onInspect: () => void;
}

interface Chip {
  readonly key: string;
  readonly label: string;
  /** The settings this chip resets to when dismissed. */
  readonly reset: SendSettings;
}

const EFFORT_LABEL: Readonly<Record<SendSettings["reasoningEffort"], string>> = {
  low: "light",
  medium: "medium",
  high: "deep",
};

export function SettingChips({
  settings,
  defaults,
  onChange,
  onInspect,
}: SettingChipsProps) {
  const chips = describeOverrides(settings, defaults);
  if (chips.length === 0) return null;

  return (
    <>
      {chips.map((chip) => (
        <Badge
          key={chip.key}
          variant="outline"
          className="gap-1 border-primary/40 bg-brand-soft py-0.5 pr-1 font-normal text-primary"
        >
          <button type="button" onClick={onInspect} className="cursor-pointer">
            {chip.label}
          </button>
          <button
            type="button"
            onClick={() => onChange(chip.reset)}
            aria-label={`Reset ${chip.label}`}
            className="cursor-pointer rounded-full p-0.5 hover:bg-primary/15"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
    </>
  );
}

/** Exported for the test that pins each chip to the setting it stands for. */
export function describeOverrides(
  settings: SendSettings,
  defaults: SendSettings,
): readonly Chip[] {
  const chips: Chip[] = [];

  if (settings.model !== defaults.model) {
    const model = MODELS.find((entry) => entry.id === settings.model);
    chips.push({
      key: "model",
      label: model?.label ?? settings.model,
      reset: { ...settings, model: defaults.model },
    });
  }

  if (settings.supervised !== defaults.supervised) {
    chips.push({
      key: "supervised",
      label: settings.supervised ? "Panel on" : "Panel off",
      reset: { ...settings, supervised: defaults.supervised },
    });
  }

  if (settings.thinking !== defaults.thinking) {
    chips.push({
      key: "thinking",
      label: settings.thinking
        ? `Think · ${EFFORT_LABEL[settings.reasoningEffort]}`
        : "No thinking",
      reset: {
        ...settings,
        thinking: defaults.thinking,
        reasoningEffort: defaults.reasoningEffort,
      },
    });
  }

  return chips;
}
